import React, { useCallback, useMemo } from 'react';
import { useLockFn } from 'ahooks';

import cx from './index.module.scss';

interface Props {
  className?: string;
  disabled?: boolean;
  disabledForAdjust?: boolean;
  value: string | number | [number, number];
  steps: { value: string; label: string }[];
  onChange: (value: string) => void | Promise<void>;
  activeColor?: 'blue' | 'white' | 'lightBlue';
  activeBold?: boolean;
  labelWidth?: number;
}

const SlideSelect: React.FC<Props> = ({
  value,
  onChange,
  className,
  activeBold,
  labelWidth,
  disabled = false,
  disabledForAdjust = false,
  steps = [],
  activeColor = 'blue',
}) => {
  const calcSliderOffset = useMemo(() => {
    const valueIdx = steps?.findIndex(item => String(item.value) === String(value));
    return valueIdx === -1 ? '-100%' : `${valueIdx * (100 / steps?.length)}%`;
  }, [steps, value]);

  const handleOnClickItem = useLockFn(
    useCallback(
      async (item: typeof steps[0]) => {
        if (disabled || disabledForAdjust) return;

        if (item.value !== value) {
          await onChange?.(item.value);
        }
      },
      [disabled, disabledForAdjust, onChange, value]
    )
  );

  return (
    <div
      className={cx('slide-select', className, {
        disabled,
        show: steps?.length > 0,
        'bg-white': activeColor === 'lightBlue',
      })}
    >
      <div className={cx('slide-box', { 'over-width': steps?.length > 4 })}>
        {steps?.map(item => (
          <div
            style={{
              ...(labelWidth ? { width: `${labelWidth}px` } : {}),
            }}
            className={cx(
              'slide-item',
              'custom-slide-item',
              { active: String(item.value) === String(value) },
              { 'active-blue': activeColor === 'blue' && String(item.value) === String(value) },
              { 'active-light-blue': activeColor === 'lightBlue' && String(item.value) === String(value) },
              { 'active-bold': activeBold && String(item.value) === String(value) }
            )}
            onClick={() => handleOnClickItem(item)}
            key={item.value}
          >
            {item.label}
          </div>
        ))}
        <div className={cx('slider-wrapper')} style={{ width: `${100 / steps?.length}%`, left: calcSliderOffset }}>
          <div
            className={cx('slider', 'custom-slider', {
              'active-blue': activeColor === 'blue',
              'active-light-blue': activeColor === 'lightBlue',
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default SlideSelect;
